import type { ProceduralTextureSpec } from "./ThemeManifest.js";

/**
 * 에셋 테이블 행 — 그래픽 에셋 데이터 시트(TSV → JSON export)의 1행과 1:1 대응.
 * 모든 그래픽 에셋(파일 이미지 + 절차 텍스처)을 코드가 아닌 데이터 1장으로 제어하기 위한 입력으로,
 * 숫자 셀은 nullable(int?) export를 전제한다 — 빈 셀은 null로 들어와 "미설정"으로 처리된다.
 * category/usedIn은 사람이 읽는 메타(분류·사용처)로 변환에는 쓰이지 않는다 — 기입 강제는 앱 테스트 책임.
 */
export interface ThemeAssetRow {
  readonly key: string;
  /** 분류 라벨(hud/gameview/tabs/ui 등) — 문서·필터 용도. */
  readonly category?: string | null;
  /** "file" | "roundedRect" | "circle" | "rect" — ProceduralTextureSpec.kind와 동일 어휘. */
  readonly kind: string;
  /** kind=file 전용 — public 기준 상대 경로(루트 "/" 시작 금지 — validateManifest가 검증). */
  readonly path?: string | null;
  readonly width?: number | null;
  readonly height?: number | null;
  readonly radius?: number | null;
  readonly fill?: string | null;
  /** 외곽선 색 — strokeWidth와 짝으로만 유효 (roundedRect 전용). */
  readonly stroke?: string | null;
  readonly strokeWidth?: number | null;
  /** 이 에셋이 게임 어디에 쓰이는지 사람용 설명. */
  readonly usedIn?: string | null;
}

/**
 * 절차 텍스처 크기 override — 도메인 SSoT(예: 밸런스 GRID/COMBAT)가 소유한 크기를
 * 테이블 값보다 우선 주입한다. 테이블 셀을 비우고 여기서 채우는 패턴이 기본.
 */
export interface ProceduralSizeOverride {
  readonly width?: number;
  readonly height?: number;
  readonly radius?: number;
}

export interface BuildManifestAssetsOptions<
  ImageKey extends string,
  ProceduralKey extends string,
> {
  /** 앱 이미지 키 SSoT — 테이블과 1:1 완전 일치해야 한다 (누락/초과/오타 = throw). */
  readonly imageKeys: readonly ImageKey[];
  /** 앱 절차 텍스처 키 SSoT — 동일 완전성 검증. */
  readonly proceduralKeys: readonly ProceduralKey[];
  readonly proceduralSizeOverrides?: Readonly<
    Partial<Record<ProceduralKey, ProceduralSizeOverride>>
  >;
}

/** buildManifestAssets 결과 — ThemeManifest의 images/procedural 조각. */
export interface ManifestAssets<ImageKey extends string, ProceduralKey extends string> {
  readonly images: Readonly<Record<ImageKey, string>>;
  readonly procedural: Readonly<Record<ProceduralKey, ProceduralTextureSpec>>;
}

/** 빈 문자열/공백/null을 "미설정"으로 정규화 — TSV string 빈 셀은 ""로 export되기 때문. */
function asText(value: string | null | undefined): string | null {
  const text = value?.trim();
  return text ? text : null;
}

function requireText(value: string | null | undefined, key: string, field: string): string {
  const text = asText(value);
  if (text == null) {
    throw new Error(`[themeAssets] '${key}' 행에 ${field}가 필요합니다`);
  }
  return text;
}

function requireNumber(value: number | null | undefined, key: string, field: string): number {
  if (value == null) {
    throw new Error(
      `[themeAssets] '${key}' 행에 ${field}가 필요합니다 (테이블 셀 또는 proceduralSizeOverrides)`,
    );
  }
  return value;
}

function toStroke(row: ThemeAssetRow): { readonly color: string; readonly width: number } | null {
  const color = asText(row.stroke);
  const width = row.strokeWidth ?? null;
  if (color == null && width == null) return null;
  if (color == null || width == null) {
    throw new Error(`[themeAssets] '${row.key}' stroke(색)·strokeWidth는 짝으로만 허용됩니다`);
  }
  return { color, width };
}

function toProceduralSpec(
  row: ThemeAssetRow,
  override: ProceduralSizeOverride | undefined,
): ProceduralTextureSpec {
  const key = row.key;
  const kind = asText(row.kind);
  // stroke 지원 판정을 짝 검증보다 먼저 — file+stroke 단독 같은 입력에 정확한 사유로 throw.
  const hasStrokeInput = asText(row.stroke) != null || row.strokeWidth != null;
  if (hasStrokeInput && kind !== "roundedRect") {
    throw new Error(`[themeAssets] '${key}' stroke는 roundedRect에서만 지원됩니다`);
  }
  const stroke = kind === "roundedRect" ? toStroke(row) : null;
  const width = override?.width ?? row.width;
  const height = override?.height ?? row.height;
  const radius = override?.radius ?? row.radius;

  switch (kind) {
    case "file":
      return { kind: "file", path: requireText(row.path, key, "path") };
    case "roundedRect":
      return {
        kind: "roundedRect",
        width: requireNumber(width, key, "width"),
        height: requireNumber(height, key, "height"),
        radius: requireNumber(radius, key, "radius"),
        fill: requireText(row.fill, key, "fill"),
        ...(stroke ? { stroke } : {}),
      };
    case "circle":
      return {
        kind: "circle",
        radius: requireNumber(radius, key, "radius"),
        fill: requireText(row.fill, key, "fill"),
      };
    case "rect":
      return {
        kind: "rect",
        width: requireNumber(width, key, "width"),
        height: requireNumber(height, key, "height"),
        fill: requireText(row.fill, key, "fill"),
      };
    default:
      throw new Error(`[themeAssets] '${key}' 지원하지 않는 kind: ${row.kind}`);
  }
}

/**
 * 에셋 테이블 행들을 ThemeManifest의 images/procedural 조각으로 조립한다.
 *
 * satisfies가 잡던 키 완결성(누락/오타)을 데이터 도입 후에도 잃지 않도록,
 * 키 SSoT 리스트와의 1:1 완전 일치를 모듈 로드/테스트 시점에 throw로 강제한다:
 * - 테이블에 없는 SSoT 키(누락), SSoT에 없는 테이블 키(미지/오타), 중복 키 → throw
 * - 이미지 키는 kind=file 강제, kind별 필수 필드(path/width/height/radius/fill) 검증
 * - 절차 키는 kind=file로 PNG 대체 가능 (proceduralFileEntries가 preload로 처리)
 *
 * 값 형식(hex·경로·양수 크기) 검증은 validateManifest 책임 — 여기서 중복하지 않는다.
 */
export function buildManifestAssets<ImageKey extends string, ProceduralKey extends string>(
  rows: readonly ThemeAssetRow[],
  options: BuildManifestAssetsOptions<ImageKey, ProceduralKey>,
): ManifestAssets<ImageKey, ProceduralKey> {
  const imageKeySet = new Set<string>(options.imageKeys);
  const proceduralKeySet = new Set<string>(options.proceduralKeys);
  const images: Partial<Record<ImageKey, string>> = {};
  const procedural: Partial<Record<ProceduralKey, ProceduralTextureSpec>> = {};
  const seen = new Set<string>();

  for (const row of rows) {
    const key = asText(row.key);
    if (key == null) throw new Error("[themeAssets] key가 빈 행이 있습니다");
    if (seen.has(key)) throw new Error(`[themeAssets] 키 중복: ${key}`);
    seen.add(key);

    if (imageKeySet.has(key)) {
      if (asText(row.kind) !== "file") {
        throw new Error(
          `[themeAssets] 이미지 키 '${key}'는 kind=file이어야 합니다 (현재: ${row.kind})`,
        );
      }
      images[key as ImageKey] = requireText(row.path, key, "path");
    } else if (proceduralKeySet.has(key)) {
      procedural[key as ProceduralKey] = toProceduralSpec(
        row,
        options.proceduralSizeOverrides?.[key as ProceduralKey],
      );
    } else {
      throw new Error(
        `[themeAssets] 미지의 키 '${key}' — imageKeys/proceduralKeys SSoT에 없습니다 (오타 또는 키 미등록)`,
      );
    }
  }

  const missing: string[] = [
    ...options.imageKeys.filter((k) => images[k] == null),
    ...options.proceduralKeys.filter((k) => procedural[k] == null),
  ];
  if (missing.length > 0) {
    throw new Error(`[themeAssets] 테이블에 누락된 키 ${missing.length}건: ${missing.join(", ")}`);
  }

  return {
    images: images as Record<ImageKey, string>,
    procedural: procedural as Record<ProceduralKey, ProceduralTextureSpec>,
  };
}
